import { GraphQLTypeNameData, GRAPHQL_TYPENAMES } from 'modules/graphql/types';

import { CurrentSelection, FolderableItem, FolderableFolder, SelectionTypes } from './types';

/**
 * Is Item Type
 *
 * Helper function to generate a type guard for Graphql Types
 * @param type GRAPHQL_TYPENAMES
 * @returns Type guard function to check if a given item has the same GraphQL type
 */
export function isItemType<TypeName extends GRAPHQL_TYPENAMES>(type: TypeName) {
  return (item: GraphQLTypeNameData<GRAPHQL_TYPENAMES>): item is GraphQLTypeNameData<TypeName> =>
    item.__typename === type;
}

/**
 * Is Item Type
 *
 * Helper function to generate a type guard for Graphql Types
 * @param type GRAPHQL_TYPENAMES
 * @returns Type guard function to check if a given item has the same GraphQL type
 */
export function isItemInTypes<TypeName extends GRAPHQL_TYPENAMES>(types: TypeName[]) {
  return (item: GraphQLTypeNameData<GRAPHQL_TYPENAMES>): item is GraphQLTypeNameData<TypeName> =>
    types.includes(item.__typename as TypeName);
}

export const isItemFolder = isItemInTypes([
  GRAPHQL_TYPENAMES.CAMPAIGN_SUBFOLDER,
  GRAPHQL_TYPENAMES.TEMPLATE_SUBFOLDER,
  GRAPHQL_TYPENAMES.JOURNEY_SUBFOLDER,
  GRAPHQL_TYPENAMES.ASSET_SUBFOLDER,
]);

export function selectId<ContentId = number, ContentName = string>(
  obj: FolderableItem<ContentId, ContentName> | FolderableFolder,
) {
  return obj.id;
}

export function isItemFolderType<ContentId = number, ContentName = string>(
  item: FolderableItem<ContentId, ContentName> | FolderableFolder,
): item is FolderableFolder {
  return isItemFolder(item);
}

export function marshallItem<ContentId = number, ContentName = string>(
  item: FolderableItem<ContentId, ContentName> | FolderableFolder,
): CurrentSelection<ContentId, ContentName> {
  if (isItemFolderType(item)) {
    return {
      node: item,
      id: item.id,
      name: item.name,
      type: SelectionTypes.Folder,
    };
  }

  return {
    node: item,
    id: item.id,
    name: item.name,
    type: SelectionTypes.Content,
  };
}

const isSelectionType = (type: SelectionTypes) => (item: CurrentSelection) => item.type === type;

export const isSelectionFolder = isSelectionType(SelectionTypes.Folder);

export const isSelectionContent = isSelectionType(SelectionTypes.Content);
